import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { EASE } from "@/lib/motion";

const identifyLines = (lines) => {
  const occurrences = new Map();
  return lines.map((line) => {
    const occurrence = occurrences.get(line) || 0;
    occurrences.set(line, occurrence + 1);
    return { id: `${line}-${occurrence}`, line };
  });
};

/* Line-by-line masked reveal.
   The observer sits on the wrapper — the inner line starts outside the
   clipping box, so observing the line itself would never intersect. */
export const MaskedLines = ({
  lines,
  className = "",
  lineClassName = "",
  delay = 0,
  as: Tag = "h2",
  testId,
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Tag ref={ref} className={className} data-testid={testId}>
      {identifyLines(lines).map(({ id, line }, position) => (
        <span key={id} className="mask-line">
          <motion.span
            className={`block ${lineClassName}`}
            initial={{ y: "112%" }}
            animate={inView ? { y: 0 } : { y: "112%" }}
            transition={{ duration: 1.05, ease: EASE, delay: (position + delay) * 0.08 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

/* Clip-path reveal. The observer sits on the un-clipped wrapper, because a
   fully clipped element never reports an intersection. */
export const ClipReveal = ({ className = "", children, duration = 1.15 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <div ref={ref} className={className}>
      <motion.div
        initial={{ clipPath: "inset(0 0 100% 0)" }}
        animate={inView ? { clipPath: "inset(0 0 0% 0)" } : {}}
        transition={{ duration, ease: EASE }}
        className="h-full w-full"
      >
        {children}
      </motion.div>
    </div>
  );
};

export const SectionTag = ({ children, num }) => (
  <div className="mb-10 flex flex-wrap items-center gap-4 md:mb-14">
    <span className="label text-[#d4ff00]">{num}</span>
    <span className="h-[1px] w-8 bg-[#d4ff00]/50" />
    <span className="label text-[#a3a3a3]">{children}</span>
  </div>
);
